import React from "react";
import { cn } from "@repo/utils/cn";
import { tv, VariantProps } from "tailwind-variants";
import { sectionWrapper } from "../sectionWrappers";
import { CardArticle, CardArticleProps } from "../CardArticle";
import { Pagination } from "../Pagination";
import type { FilterField } from "./FilterSection";

const cardGridWithFilters = tv({
  base: "uipm:w-full",
});

const gridContainer = tv({
  base: "uipm:py-8",
});

const grid = tv({
  base: "uipm:grid uipm:grid-cols-1 uipm:md:grid-cols-2 uipm:lg:grid-cols-3 uipm:xl:grid-cols-4 uipm:gap-[var(--container-gutter)]",
});

const paginationContainer = tv({
  base: "uipm:flex uipm:justify-center uipm:mt-8",
});

const emptyState = tv({
  base: cn(
    "uipm:flex uipm:items-center uipm:justify-center",
    "uipm:py-16 uipm:px-4",
    "uipm:!text-[var(--typography-colors-text-faded,#828282)]",
    "uipm:font-['Inter'] uipm:font-medium",
    "uipm:text-[length:var(--typography-sizes-body-medium,16px)]",
    "uipm:leading-[var(--typography-line-heights-body-medium,26px)]",
  ),
});

export type ArticleData = CardArticleProps & { id?: string };

export interface FilterData {
  key: string;
  label?: string;
  value?: string | string[];
  options?: Array<{
    label: string;
    value: string;
  }>;
  onValueChange?: (value: string) => void;
}

export type CardGridWithFiltersProps = VariantProps<typeof sectionWrapper> &
  VariantProps<typeof cardGridWithFilters> & {
    id?: string;
    className?: string;
    articles: ArticleData[];
    filters?: FilterData[];
    searchQuery?: string;
    searchPlaceholder?: string;
    inputBackground?: "default" | "white";
    cardsBackground?:
      | ""
      | "none"
      | "accent"
      | "accent-light"
      | "light"
      | "very-light";
    currentPage: number;
    totalPages: number;
    buildPageUrl: (page: number) => string;
    imageAspectRatio?: string;
    hideSpecificFilters?: string[];
    onSearchChange?: (query: string) => void;
    onFilterChange?: (filterKey: string, selectedTitle: string) => void;
    onArticleClick?: (
      title?: string,
      date?: string,
      linkText?: string,
      linkHref?: string,
    ) => void;
    filtersSlot?: React.ReactNode;
    emptyStateText?: string;
  };

export function CardGridWithFilters({
  className,
  backgroundColor = "none",
  topPadding = "none",
  bottomPadding = "none",
  articles,
  cardsBackground = "none",
  currentPage,
  totalPages,
  buildPageUrl,
  imageAspectRatio = "3/2",
  onArticleClick,
  filtersSlot,
  emptyStateText = "No results found",
  id,
  ...props
}: CardGridWithFiltersProps) {
  // Map cardsBackground to valid CardArticle backgroundColor values
  const cardBackgroundColor: CardArticleProps["backgroundColor"] =
    cardsBackground === "accent" || cardsBackground === "accent-light"
      ? "primary"
      : cardsBackground === "very-light"
        ? "very-light"
        : cardsBackground || "none";

  return (
    <div
      className={cn(
        sectionWrapper({ backgroundColor, topPadding, bottomPadding }),
        totalPages > 1 ? "uipm:pb-0" : "",
      )}
    >
      <section
        id={id}
        className={cn(cardGridWithFilters(), className, "uipm:pb-0")}
        {...props}
      >
        {filtersSlot}

        <div className="uipm:max-w-[1440px] uipm:mx-auto uipm:md:px-[var(--container-margin)]">
          {articles.length === 0 ? (
            <div className={emptyState()}>{emptyStateText}</div>
          ) : (
            <div className={totalPages > 1 ? gridContainer() : ""}>
              <div className={grid()}>
                {articles.map((article, index) => (
                  <CardArticle
                    key={article.id || index}
                    layout="vertical"
                    backgroundColor={cardBackgroundColor}
                    verticalPadding="default"
                    horizontalPadding="default"
                    image={
                      article.image
                        ? {
                            ...article.image,
                            aspectRatio: imageAspectRatio,
                          }
                        : undefined
                    }
                    title={article.title}
                    text={article.text}
                    link={article.link}
                    date={article.date}
                    onCardClick={onArticleClick}
                  />
                ))}
              </div>

              {totalPages > 1 && (
                <div className={paginationContainer()}>
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    buildPageUrl={buildPageUrl}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
